import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { InputField, CheckboxField, TextareaField ,RadioField} from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { fetchPurposeOptions, MasterOption ,fetchMailOptOptions} from '@/data';
import { removeEmoji, zenkakuToHankaku } from '@/utiils';

// -------------------- schema --------------------
const schemaBase = z.object({
  zip_current: z.string()
    .min(1, { message: '郵便番号は必須です' })
    .regex(/^\d{3}-?\d{4}$/, { message: '正しい形式で入力してください' }),
  address_current: z.string().min(1, { message: '現住所は必須です' }),
  amc: z.string().regex(/^(|\d{10})$/, { message: 'ANAマイレージクラブお客様番号は10桁の半角数字で入力してください' }),
  mail_opt: z.string().min(1, { message: "二地域居住にとっておすすめのメール配信を希望するは必須です" }),
  phone: z.string()
    .min(1, { message: '電話番号は必須です' })
    .regex(/^0[-\d]{9,12}$/, { message: '正しい形式で入力してください' }),
  purpose_detail: z.string()
    // .min(1, { message: '具体的な目的は必須です' })
    // .min(20, { message: '具体的な目的は20〜200文字で入力してください' })
    // .max(200, { message: '具体的な目的は20〜200文字で入力してください' })
    .regex(/^(?!.*(?:<>|<[^>]*>|https?:|www\.|ftp:))[\s\S]*$/, { message: 'URL、絵文字、HTMLタグは使用できません' }),
});

const schemaPurpose = z.object({
  purpose: z.array(z.string())
    .min(1, { message: '二地域居住の目的は必須です' })
    .max(8, { message: '二地域居住の目的は1〜8つまで選択できます' }),
  purpose_other: z.string().trim().optional(),
}).superRefine((data, ctx) => {
  const pickedOther = data.purpose.includes('99');
  const txt = (data.purpose_other ?? '').trim();
  if (pickedOther && !txt) {
    ctx.addIssue({
      code: 'custom',
      message: 'その他を選択の場合、記入は必須です',
      path: ['purpose_other'],
    });
  }
  if (pickedOther && txt && txt.length > 80) {
    ctx.addIssue({
      code: 'custom',
      message: 'その他の内容は80文字以内で入力してください',
      path: ['purpose_other'],
    });
  }
});

const schema = schemaBase.and(schemaPurpose);

// -------------------- types --------------------
export type EditRequestFormData = z.infer<typeof schema>;

export type EditRequestFormHandle = {
  validateAndGet: () => Promise<{ ok: boolean; data?: EditRequestFormData }>;
};

type Props = {
  enabled: boolean;
  shouldSubmit: boolean;
  onValidationChange: (d: { isValid: boolean; formData: EditRequestFormData | undefined }) => void;
  // purposeCsv は "1,2,99" のような初期CSV用（配列を渡せるなら purpose を直接でもOK）
  initialValues?: Partial<EditRequestFormData> & { purposeCsv?: string };
};

// -------------------- component --------------------
const EditRequestForm = forwardRef<EditRequestFormHandle, Props>(function EditRequestForm(
  { enabled, shouldSubmit, onValidationChange, initialValues },
  ref
) {
  const {
    register,
    reset,
    getValues,
    setValue,
    watch,
    trigger,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<EditRequestFormData>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: {
      zip_current: '',
      address_current: '',
      amc: '',
      phone: '',
      purpose: [],
      purpose_detail: '',
      purpose_other: '',
      mail_opt:'',
    },
  });

  const [purposeOptions, setPurposeOptions] = useState<MasterOption[]>([]);
  const [mailOptOptions, setMailOptions] = useState<MasterOption[]>([]);

  // マスター取得
  useEffect(() => {
    (async () => {
      try {
        const purpose = await fetchPurposeOptions();
        setPurposeOptions(purpose);
        const mailOptOptions = await fetchMailOptOptions();
        setMailOptions(mailOptOptions);
      } catch (e) {
        console.error('failed to load purpose options', e);
      }
    })();
  }, []);

  // 初期値投入
  useEffect(() => {
    if (!initialValues) return;
    const toArray = (csv?: string) =>
      (csv ?? '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

    reset({
      zip_current: initialValues.zip_current ?? '',
      address_current: initialValues.address_current ?? '',
      amc: initialValues.amc ?? '',
      phone: initialValues.phone ?? '',
      purpose: initialValues.purpose ?? toArray(initialValues.purposeCsv),
      purpose_detail: initialValues.purpose_detail ?? '',
      purpose_other: initialValues.purpose_other ?? '',
      mail_opt: initialValues.mail_opt ?? '',
    }, { keepErrors: false });
console.log(initialValues);
    // 初期表示時にエラーハイライトも反映
    setTimeout(() => trigger(), 0);
  }, [initialValues, reset, trigger]);

  // 親への随時通知（zip/phoneは見た目のハイフンを除去して渡す）
  useEffect(() => {
    const sub = watch(() => {
      const v = getValues();
      const safe: EditRequestFormData = {
        ...v,
        zip_current: (v.zip_current || '').replace(/-/g, ''),
        phone: (v.phone || '').replace(/-/g, ''),
      };
      onValidationChange({ isValid, formData: isValid ? safe : undefined });
    });
    return () => sub.unsubscribe();
  }, [watch, getValues, isValid, onValidationChange]);

  // 親からの submit 指示
  useEffect(() => {
    if (shouldSubmit) trigger();
  }, [shouldSubmit, trigger]);

  // 「その他」チェックが外れたら自由記入をクリア
  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (name === 'purpose') {
        const arr = (value.purpose ?? []) as string[];
        if (!Array.isArray(arr) || !arr.includes('99')) {
          setValue('purpose_other', '');
          trigger('purpose_other');
        }
      }
    });
    return () => sub.unsubscribe();
  }, [watch, setValue, trigger]);

  // 親から呼べる validateAndGet
  useImperativeHandle(ref, () => ({
    validateAndGet: async () => {
      const ok = await trigger();
      if (!ok) return { ok: false };
      const v = getValues();
      const data: EditRequestFormData = {
        ...v,
        zip_current: (v.zip_current || '').replace(/-/g, ''),
        phone: (v.phone || '').replace(/-/g, ''),
      };
      return { ok: true, data };
    },
  }));

  // UI
  const pickedOther = Array.isArray(watch('purpose')) && watch('purpose').includes('99');

  return (
    <>
      <div className="xmd:max-w-[40rem]">
        <InputField
          label="郵便番号"
          type="text"
          register={register('zip_current')}
          errors={errors}
          hasRequiredLabel
          disabled={!enabled}
          onInput={(e) => zenkakuToHankaku(e.target as HTMLInputElement)}
        />
      </div>

      <InputField
        label="住所"
        type="text"
        register={register('address_current')}
        errors={errors}
        hasRequiredLabel
        disabled={!enabled}
        onInput={(e) => removeEmoji(e.target as HTMLInputElement)}
      />

      <div className="xmd:max-w-[40rem]">
        <InputField
          label="ANAマイレージクラブお客様番号"
          type="text"
          register={register('amc')}
          errors={errors}
          disabled={!enabled}
          onInput={(e) => zenkakuToHankaku(e.target as HTMLInputElement)}
        />
      </div>
      <div className="xmd:max-w-[40rem]">
        <RadioField 
          label="二地域居住にとっておすすめのメール配信を希望する" 
          options={mailOptOptions} register={register('mail_opt')} 
          errors={errors} 
          hasRequiredLabel={true} 
          disabled={!enabled} 
          />
      </div>      

      <div className="xmd:max-w-[40rem]">
        <InputField
          label="電話番号"
          type="text"
          register={register('phone')}
          errors={errors}
          hasRequiredLabel
          disabled={!enabled}
          onInput={(e) => zenkakuToHankaku(e.target as HTMLInputElement)}
        />
      </div>

      <div className="flex flex-col gap-3">
        <CheckboxField
          label="二地域居住の目的"
          options={purposeOptions}
          register={register('purpose')}
          errors={errors}
          hasRequiredLabel
          disabled={!enabled}
        />

        {/* 99=その他 を選んだときだけ、同じまとまり内に自由記入欄を表示 */}
        {pickedOther && (
          <div className="sm:pl-6">
            <InputField
              // ラベルは付けず、チェック行の一部として見えるようにする
              type="text"
              placeholder="その他（自由記入）"
              register={register('purpose_other')}
              errors={errors}
              disabled={!enabled}
              onInput={(e) => removeEmoji(e.target as HTMLInputElement)}
            />
          </div>
        )}
      </div>

      <TextareaField
        label="具体的な目的"
        register={register('purpose_detail')}
        errors={errors}
        hasRequiredLabel
        disabled={!enabled}
        onInput={(e) => removeEmoji(e.target as HTMLInputElement)}
      />
    </>
  );
});

export default EditRequestForm;
